import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import {
  TrendingUp, Wallet, PiggyBank, AlertTriangle, MapPin, Target,
  GitBranch, ArrowUpRight, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  const n = Number(amount || 0);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
};

function LtvFigure({ icon: Icon, label, value, testId, tone }) {
  const toneCls = tone === 'success' ? 'text-emerald-700' : tone === 'amber' ? 'text-amber-700' : 'text-slate-900';
  return (
    <div className="flex-1 min-w-[140px] px-4 py-3 border border-slate-100 rounded-xl bg-slate-50/60" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500 font-medium">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`text-xl font-bold mt-1 ${toneCls}`}>{formatCurrency(value)}</div>
    </div>
  );
}

function StatChip({ label, value, testId }) {
  return (
    <div className="px-3 py-2 rounded-lg bg-white border border-slate-100" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">{label}</div>
      <div className="text-sm font-semibold text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}

const TravellerIntelligencePanel = ({ ucc }) => {
  const [ltv, setLtv] = useState(null);
  const [quotes, setQuotes] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      axios.get(`${BACKEND_URL}/api/clients/${ucc}/ltv-intelligence`),
      axios.get(`${BACKEND_URL}/api/clients/${ucc}/quote-intelligence`),
    ])
      .then(([lRes, qRes]) => {
        if (!active) return;
        setLtv(lRes.data);
        setQuotes(qRes.data);
      })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [ucc]);

  if (loading) {
    return (
      <Card className="p-5 bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="traveller-intelligence-card">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <RefreshCw className="h-4 w-4 animate-spin" /> Loading traveller intelligence…
        </div>
      </Card>
    );
  }
  if (!ltv || !quotes) return null;

  const destinationsVisited = ltv.destinationsVisited || [];
  const destinationsQuoted = quotes.destinationsQuoted || [];
  const profitIncompleteTrips = ltv.profitIncompleteTrips || [];

  return (
    <Card className="p-5 bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="traveller-intelligence-card">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-slate-500" /> Traveller Intelligence
        </h2>
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Deterministic · from CRM data</span>
      </div>

      {/* THREE DISTINCT LTV FIGURES — never conflated */}
      <div className="flex flex-wrap gap-2 mt-3" data-testid="ltv-three-figures">
        <LtvFigure icon={Wallet} label="Client Transaction LTV" value={ltv.clientTransactionLtv} tone="success" testId="ltv-client-transaction" />
        <LtvFigure icon={ArrowUpRight} label="Travcations Revenue LTV" value={ltv.travcationsRevenueLtv} testId="ltv-revenue" />
        <LtvFigure icon={PiggyBank} label="Travcations Profit LTV" value={ltv.travcationsProfitLtv} tone={ltv.profitDataStatus === 'partial' ? 'amber' : 'success'} testId="ltv-profit" />
      </div>
      {ltv.profitDataStatus === 'partial' && (
        <div
          className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800"
          data-testid="ltv-profit-partial-note"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Partial data — cost/revenue breakdown is missing for {profitIncompleteTrips.length} trip(s):{' '}
            <span className="font-mono">{profitIncompleteTrips.join(', ')}</span>. Profit LTV above excludes their unknown
            cost/revenue rather than assuming zero.
          </span>
        </div>
      )}

      {/* DESTINATIONS VISITED (confirmed trips only) */}
      <div className="mt-4">
        <div className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-1.5 flex items-center gap-1.5">
          <MapPin className="h-3 w-3" /> Destinations visited
        </div>
        {ltv.destinationsVisited.length === 0 ? (
          <div className="text-sm text-slate-400" data-testid="destinations-visited-empty">No confirmed trips yet</div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="destinations-visited">
            {destinationsVisited.map((d) => (
              <span
                key={d.destination}
                className="px-2.5 py-0.5 rounded-full text-xs bg-teal-50 text-teal-700 border border-teal-100"
              >
                {d.destination} <span className="text-teal-500">({d.count})</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* QUOTE FUNNEL — shared counts (Session 1 + Session 2) */}
      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-2 flex items-center gap-1.5">
          <Target className="h-3 w-3" /> Quote Intelligence
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="quote-funnel-grid">
          <StatChip label="Total quotes" value={quotes.totalQuotes} testId="qi-total" />
          <StatChip label="Accepted" value={quotes.acceptedQuotes} testId="qi-accepted" />
          <StatChip label="Rejected" value={quotes.rejectedQuotes} testId="qi-rejected" />
          <StatChip label="Expired" value={quotes.expiredQuotes} testId="qi-expired" />
          <StatChip label="Pending" value={quotes.pendingQuotes} testId="qi-pending" />
          <StatChip label="Cancelled trips" value={quotes.cancelledTrips} testId="qi-cancelled" />
          <StatChip label="Conversion rate" value={`${quotes.conversionRatePct}%`} testId="qi-conversion" />
          <StatChip label="Quote revisions" value={quotes.quoteRevisionsCount} testId="qi-revisions" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
          <StatChip label="Avg quote value" value={formatCurrency(quotes.avgQuoteValue)} testId="qi-avg-value" />
          <StatChip label="Highest quote" value={`↑ ${formatCurrency(quotes.highestQuoteValue)}`} testId="qi-highest-value" />
          <StatChip label="Lowest quote" value={`↓ ${formatCurrency(quotes.lowestQuoteValue)}`} testId="qi-lowest-value" />
          <StatChip
            label="Avg decision time"
            value={quotes.avgDecisionTimeDays === null ? 'No data' : `${quotes.avgDecisionTimeDays}d (n=${quotes.decisionTimeSampleSize})`}
            testId="qi-decision-time"
          />
        </div>

        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium mb-1.5 flex items-center gap-1.5">
            <GitBranch className="h-3 w-3" /> Destinations quoted (all quotes)
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="destinations-quoted">
            {destinationsQuoted.length === 0
              ? <span className="text-sm text-slate-400">—</span>
              : destinationsQuoted.map((d) => (
                <span key={d} className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 text-slate-600">{d}</span>
              ))}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default TravellerIntelligencePanel;
